/**
 * marketingSchema.js — homepage-only JSON-LD (Sept 2026 SEO audit fix).
 *
 * These blocks used to live in public/index.html, which shipped them on
 * EVERY route (coach pages, login variants, checkout…). Google's Rich
 * Results validator flagged that at scale — most notably FAQPage markup
 * on pages where the FAQ content isn't visible. They now render only on
 * the Landing page via <Seo jsonLd={HOMEPAGE_JSON_LD} />.
 *
 * (An array is valid JSON-LD: one <script> tag containing [{...},{...}].)
 * The two site-wide Organization blocks intentionally REMAIN in
 * index.html — global entity markup is correct there.
 */
export const HOMEPAGE_JSON_LD = [
    {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': 'https://splashpad.pro/#software',
        name: 'SplashPad',
        alternateName: ['Splash Pad', 'SplashPad by SSU'],
        url: 'https://splashpad.pro',
        operatingSystem: 'Web',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Swim School Management Software',
        description:
            'All-in-one swim school software for small swim schools and coaches — built by a swim school owner. Branded registration pages, digital waivers, automated Stripe payments, coach-to-parent credits, a staff portal with shifts, time clock and payroll export, per-location performance reporting, and a worldwide public coach directory.',
        brand: { '@id': 'https://splashpad.pro/#organization' },
        publisher: { '@id': 'https://splashpad.pro/#organization' },
        featureList: [
            'Branded parent-facing registration pages',
            'Drag-and-drop landing page visual editor',
            'Digital waivers with e-signature and versioning',
            'Automated Stripe Connect Express payments',
            'Card-on-file charging with parent consent',
            'Coach-to-parent credits (grant, refund-to-credit, split refunds, apply-at-charge)',
            'Attendance tracking with automatic session-decrement',
            'Staff portal with schedules, shifts, and mobile timeclock',
            'Payroll export (PDF and CSV) with per-location breakdown',
            'Per-location revenue, enrollment and staff-hour reporting',
            'Public worldwide coach directory listing every Pro coach',
            'Two-factor authentication (TOTP) and HaveIBeenPwned breach-checked passwords',
            'Free 30-day trial with full access',
        ],
        audience: {
            '@type': 'Audience',
            audienceType: 'Small swim schools, swim school owners with staff, and independent swim coaches',
        },
        offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '0',
            highPrice: '79.99',
            offerCount: '3',
            offers: [
                {
                    '@type': 'Offer',
                    name: 'Free 30-day trial',
                    price: '0',
                    priceCurrency: 'USD',
                    description: 'Full access to SplashPad for 30 days. Cancel anytime before day 30.',
                    url: 'https://splashpad.pro/signup',
                    availability: 'https://schema.org/InStock',
                    category: 'Trial',
                },
                {
                    '@type': 'Offer',
                    name: 'Starter',
                    price: '39.99',
                    priceCurrency: 'USD',
                    description:
                        'Everything a solo coach or brand-new swim school needs to launch. Supports up to 40 active students. Flat monthly price plus just $0.25 per transaction.',
                    url: 'https://splashpad.pro/pricing',
                    availability: 'https://schema.org/InStock',
                    priceSpecification: {
                        '@type': 'UnitPriceSpecification',
                        price: '39.99',
                        priceCurrency: 'USD',
                        unitCode: 'MON',
                        billingIncrement: 1,
                    },
                },
                {
                    '@type': 'Offer',
                    name: 'Pro',
                    price: '79.99',
                    priceCurrency: 'USD',
                    description:
                        'The all-in-one system for swim schools with staff. Unlimited students, drag-and-drop website, staff portal with shifts, time clock and payroll export, per-location reporting, branded emails and broadcasts, plus a personal 1-on-1 onboarding call.',
                    url: 'https://splashpad.pro/pricing',
                    availability: 'https://schema.org/InStock',
                    priceSpecification: {
                        '@type': 'UnitPriceSpecification',
                        price: '79.99',
                        priceCurrency: 'USD',
                        unitCode: 'MON',
                        billingIncrement: 1,
                    },
                },
            ],
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            ratingCount: '32',
            bestRating: '5',
            worstRating: '1',
        },
    },
    {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Swim Lesson Registration & School Management',
        provider: { '@type': 'Organization', name: 'SplashPad', url: 'https://splashpad.pro' },
        description:
            'End-to-end swim school software — registration, digital waivers, automated Stripe payments, staff scheduling and payroll — for small swim schools and independent swim coaches.',
        areaServed: 'Worldwide',
        serviceType: 'Swim Lesson Registration Software',
    },
    {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: 'What is the best swim school software for small swim schools and coaches?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'SplashPad is swim school software built by a swim school owner for small swim schools and coaches. It gives you branded registration pages, digital waivers, automated Stripe payments, coach-to-parent credits, a staff portal with shifts, time clock and payroll export, per-location reporting, and a worldwide public coach directory in one platform — without the enterprise pricing of Jackrabbit or iClassPro.',
                },
            },
            {
                '@type': 'Question',
                name: 'Who built SplashPad?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'SplashPad was designed by Jacob Siegel, a working swim instructor and swim school owner, with the Superhero Swim University community of swim school owners and coaches. Every workflow — from waivers to credits to staff timeclock and payroll — is shaped by real people running real swim schools.',
                },
            },
            {
                '@type': 'Question',
                name: 'Does SplashPad handle payments, refunds, and credits?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. SplashPad uses Stripe Connect so parents pay coaches directly. Coaches can charge card-on-file, split refunds between card and account credit, grant credits ad-hoc, and apply credit at checkout. Every money move writes an immutable audit-log row.',
                },
            },
            {
                '@type': 'Question',
                name: 'Is there a free trial?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. Every new coach gets a free 30-day trial of SplashPad Pro with full access to registration pages, payments, staff tools, and the coach directory. Cancel anytime before day 30.',
                },
            },
            {
                '@type': 'Question',
                name: 'Is SplashPad secure?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. SplashPad uses httpOnly session cookies, TOTP two-factor authentication, HaveIBeenPwned breach-checked passwords, IP rate limiting, account lockouts, and hardened HTTP security headers. Card data is captured directly by Stripe — SplashPad never sees or stores it.',
                },
            },
        ],
    },
];
